import { Formik, FormikProps } from "formik";
import get from "lodash-es/get";
import React, { FormEvent, PureComponent, ReactNode } from "react";
import { RouteComponentProps } from "react-router-dom";
import styled from "styled-components";

import BlockWrapper from "../../../components/BlockWrapper";
import { Input } from "../../../components/Form";
import { ILoginProps } from "../../../hoc/persister/login/login";
import {
  getErrorMessage,
  isInputInvalid,
} from "../../../services/formValidation.service";
import { translate } from "../../../services/translation.service";
import theme, { media } from "../../../theme";

import PasswordRecoveryFunnel from "./components/PasswordRecoveryFunnel";
import SignInSchema from "./validationSchema";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  ${media.laptop} {
    margin-top: 0;
  }
`;

const FormFieldsWrapper = styled.form`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  & > *:not(:first-child) {
    margin-top: 16px;
  }
`;

const Text = styled.div`
  color: ${theme.colors.brown};
  font-size: ${theme.fontSizes.small};
  letter-spacing: 0.47pt;
`;

const Button = styled.button`
  margin-top: 60px;
  border: none;
  background-color: transparent;
  font-family: ${theme.fonts.subtitle};
  font-size: 18px;
  letter-spacing: 0.7pt;
`;

const FormRowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Error = styled.div`
  color: ${theme.colors.red};
  font-size: ${theme.fontSizes.small};
  letter-spacing: 0.47pt;
`;

const Submit = styled.input`
  display: none;
`;

export interface IFormikProps {
  email: string;
  password: string;
}

export interface IProps extends RouteComponentProps, ILoginProps {
  useReplace?: boolean;
  redirectUrl: string;
  signUpUrl: string;
  className?: string;
}

export interface IState {
  areCredentialsInvalid: boolean;
  openPasswordRecoveryFunnel: boolean;
}

export default class SignInForm extends PureComponent<IProps, IState> {
  public state: IState = {
    areCredentialsInvalid: false,
    openPasswordRecoveryFunnel: false,
  };

  private mounted: boolean = false;

  public componentDidMount(): void {
    this.mounted = true;
  }
  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public closePasswordRecoveryFunnel = (): void => {
    this.setState({ openPasswordRecoveryFunnel: false });
  };

  public goToSignUp = (): void => {
    const { history, signUpUrl } = this.props;
    history.replace(signUpUrl);
  };

  public isFormInvalid = (formikProps: FormikProps<IFormikProps>): boolean => {
    const { errors, touched } = formikProps;

    return Object.keys(errors).some((key: string): boolean => {
      return get(touched, key);
    });
  };

  public login = async (values: IFormikProps): Promise<void> => {
    const { history, login, useReplace, redirectUrl } = this.props;
    const { email, password } = values;
    this.setState({ areCredentialsInvalid: false });

    try {
      const { error } = await login({ email, password });
      if (this.mounted && !error) {
        if (useReplace) {
          history.replace(redirectUrl);
        } else {
          history.push(redirectUrl);
        }
      }
    } catch (e) {
      if (this.mounted) {
        this.setState({ areCredentialsInvalid: true });
      }
    }
  };

  public openPasswordRecoveryFunnel = (): void => {
    this.setState({ openPasswordRecoveryFunnel: true });
  };

  public render(): ReactNode {
    return (
      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        validationSchema={SignInSchema}
        onSubmit={(values: IFormikProps): void => {
          this.login(values);
        }}
        render={(formikProps: FormikProps<IFormikProps>): ReactNode => (
          <Wrapper className={this.props.className}>
            <BlockWrapper
              title={translate("authentication.signIn")}
              validationText={translate("authentication.connection")}
              onClick={(): void => {
                formikProps.handleSubmit();
              }}
              loading={this.props.loginLoading}
              className="signin-form"
            >
              <FormFieldsWrapper
                onSubmit={(e: FormEvent): void => {
                  e.preventDefault();
                  formikProps.handleSubmit();
                }}
              >
                <Input
                  type="email"
                  value={formikProps.values.email}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  placeholder={translate("authentication.email")}
                  name="email"
                  required={true}
                  invalid={isInputInvalid<IFormikProps>(formikProps, "email")}
                  errorMessage={getErrorMessage(formikProps, "email")}
                />

                <Input
                  type="password"
                  value={formikProps.values.password}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  placeholder={translate("authentication.password")}
                  name="password"
                  required={true}
                  invalid={isInputInvalid<IFormikProps>(
                    formikProps,
                    "password"
                  )}
                  errorMessage={getErrorMessage(formikProps, "password")}
                />
                <Submit type="submit" />

                <FormRowWrapper>
                  <Text
                    className="forgotten-password"
                    onClick={this.openPasswordRecoveryFunnel}
                  >
                    {translate("authentication.forgottenPassword")}
                  </Text>
                  {this.isFormInvalid(formikProps) && (
                    <Error>*{translate("authentication.requiredFields")}</Error>
                  )}
                </FormRowWrapper>
                {this.state.areCredentialsInvalid && (
                  <Error>Email ou mot de passe invalide</Error>
                )}
              </FormFieldsWrapper>
            </BlockWrapper>
            <Button onClick={this.goToSignUp}>
              {translate("authentication.createAccount")}
            </Button>
            <PasswordRecoveryFunnel
              defaultEmail={formikProps.values.email}
              onClose={this.closePasswordRecoveryFunnel}
              open={this.state.openPasswordRecoveryFunnel}
            />
          </Wrapper>
        )}
      />
    );
  }
}
